// Sugalarm — web-dashboard "Patterns & insights" computations (cycle 178).
//
// PURE + DETERMINISTIC. Everything here is descriptive statistics over data
// the dashboard already loads — edge-triggered alarm episodes (`/api/alerts`),
// daily TIR summaries (`/api/analytics/daily`), and the window's readings
// (`/api/glucose/history`). NO LLM, NO advice, NO diagnosis — the strings
// this module emits describe *what happened* ("lows cluster overnight"),
// never *what to do*. The cycle-172 observational disclaimer already covers
// the whole dashboard, so nothing here repeats it.
//
// Hour-of-day bucketing reads each timestamp's LOCAL hour via the browser
// clock (`getHours()`), which sidesteps the deferred server-side per-user
// timezone exactly as the cycle-177 AGP fold does.

import type { Alert, AlertLevel, DailySummary, Reading } from "./types";

// Overnight window [00:00, 06:00) — the hours we visually emphasize and
// call out when lows concentrate there.
export const OVERNIGHT_START_HOUR = 0;
export const OVERNIGHT_END_HOUR = 6;

// Coverage at or below this (percent) flags the patterns as unreliable.
export const COVERAGE_LOW_PCT = 70;
// A day must clear ≥ this TIR (percent) to extend the in-target streak.
export const STREAK_TIR_PCT = 70;
// A day needs at least this many readings before it can be "best",
// "toughest", or count toward the streak — keeps a 3-reading day from
// winning on a meaningless 100% TIR.
export const MIN_DAY_READINGS = 60;

function isLow(level: AlertLevel): boolean {
  return level === "low" || level === "urgent_low";
}
function isHigh(level: AlertLevel): boolean {
  return level === "high" || level === "urgent_high";
}

export interface EpisodeBuckets {
  /** Count of LOW episodes triggered in each local hour-of-day (length 24). */
  lowsByHour: number[];
  /** Count of HIGH episodes triggered in each local hour-of-day (length 24). */
  highsByHour: number[];
  lowsTotal: number;
  highsTotal: number;
  /** Lows triggered in the overnight window [00:00, 06:00). */
  overnightLows: number;
  /** Minutes from trigger→recovery, only for lows that have recovered. */
  lowDurationsMin: number[];
}

/** Bucket the edge-triggered alarm rows whose `triggered_at` falls in
 *  [startMs, endMs] by LOCAL hour-of-day, split into lows (down) and highs
 *  (up). One row = one episode (the alerts engine is edge-triggered). */
export function bucketEpisodes(
  alerts: Alert[],
  startMs: number,
  endMs: number,
): EpisodeBuckets {
  const lowsByHour = new Array<number>(24).fill(0);
  const highsByHour = new Array<number>(24).fill(0);
  let lowsTotal = 0;
  let highsTotal = 0;
  let overnightLows = 0;
  const lowDurationsMin: number[] = [];

  for (const a of alerts) {
    const t = new Date(a.triggered_at).getTime();
    if (Number.isNaN(t) || t < startMs || t > endMs) continue;
    const hour = new Date(t).getHours();
    if (isLow(a.level)) {
      lowsByHour[hour] = lowsByHour[hour]! + 1;
      lowsTotal++;
      if (hour >= OVERNIGHT_START_HOUR && hour < OVERNIGHT_END_HOUR) {
        overnightLows++;
      }
      if (a.recovered_at) {
        const r = new Date(a.recovered_at).getTime();
        if (!Number.isNaN(r) && r > t) lowDurationsMin.push((r - t) / 60000);
      }
    } else if (isHigh(a.level)) {
      highsByHour[hour] = highsByHour[hour]! + 1;
      highsTotal++;
    }
  }

  return {
    lowsByHour,
    highsByHour,
    lowsTotal,
    highsTotal,
    overnightLows,
    lowDurationsMin,
  };
}

export interface HourWindow {
  start: number;
  end: number;
  count: number;
}

/** The 3-hour rolling window (wrapping past midnight) holding the most
 *  episodes. Returns null when there are none. */
export function modalWindow(byHour: number[]): HourWindow | null {
  const total = byHour.reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  let bestStart = 0;
  let best = -1;
  for (let s = 0; s < 24; s++) {
    let sum = 0;
    for (let k = 0; k < 3; k++) sum += byHour[(s + k) % 24]!;
    if (sum > best) {
      best = sum;
      bestStart = s;
    }
  }
  return { start: bestStart, end: (bestStart + 3) % 24, count: best };
}

/** "1 AM", "12 PM", … */
export function fmtHour12(h: number): string {
  const norm = ((h % 24) + 24) % 24;
  const period = norm < 12 ? "AM" : "PM";
  const hr = norm % 12 === 0 ? 12 : norm % 12;
  return `${hr} ${period}`;
}

/** Compact hour range, sharing the period when both ends match: "1–4 AM",
 *  else "11 AM–2 PM". */
export function fmtHourWindow(start: number, end: number): string {
  const sp = ((start % 24) + 24) % 24 < 12 ? "AM" : "PM";
  const ep = ((end % 24) + 24) % 24 < 12 ? "AM" : "PM";
  const sh = start % 12 === 0 ? 12 : start % 12;
  const eh = end % 12 === 0 ? 12 : end % 12;
  return sp === ep ? `${sh}–${eh} ${sp}` : `${sh} ${sp}–${eh} ${ep}`;
}

export interface Coverage {
  actual: number;
  expected: number;
  pct: number;
  cadenceMin: number;
  low: boolean;
}

/** CGM coverage: readings actually present vs. expected for the derived
 *  cadence over the window. Cadence is the median inter-reading gap
 *  (clamped 1–15 min, default 5) so it adapts to Dexcom (5 min) vs Libre
 *  (~1 min) without a provider assumption; gaps in the data pull the
 *  ratio below 100%. */
export function computeCoverage(
  readings: Reading[],
  windowMs: number,
): Coverage {
  const actual = readings.length;
  const times = readings
    .map((r) => new Date(r.timestamp).getTime())
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b);

  let cadenceMin = 5;
  if (times.length >= 4) {
    const gaps: number[] = [];
    for (let i = 1; i < times.length; i++) {
      const g = (times[i]! - times[i - 1]!) / 60000;
      if (g > 0 && g <= 60) gaps.push(g);
    }
    if (gaps.length) {
      gaps.sort((a, b) => a - b);
      const med = gaps[Math.floor(gaps.length / 2)]!;
      cadenceMin = Math.min(15, Math.max(1, med));
    }
  }

  const expected = Math.round(windowMs / 60000 / cadenceMin);
  const pct = expected > 0 ? Math.min(100, Math.round((actual / expected) * 100)) : 0;
  return {
    actual,
    expected,
    pct,
    cadenceMin: Math.round(cadenceMin),
    low: expected > 0 && pct < COVERAGE_LOW_PCT,
  };
}

export interface DayFact {
  date: string;
  inRangePct: number;
}

export interface Facts {
  best: DayFact | null;
  toughest: DayFact | null;
  /** Consecutive most-recent qualifying days at ≥ STREAK_TIR_PCT TIR. */
  streak: number;
}

/** Best / toughest day by in-range %, plus the current in-target streak,
 *  over daily summaries. `days` arrives most-recent-first (the
 *  `/api/analytics/daily` order), so the streak walks from index 0. Only
 *  days with ≥ MIN_DAY_READINGS readings qualify. */
export function factsFromDaily(days: DailySummary[]): Facts {
  const qualifying = days.filter((d) => d.reading_count >= MIN_DAY_READINGS);
  if (qualifying.length === 0) return { best: null, toughest: null, streak: 0 };

  let best = qualifying[0]!;
  let toughest = qualifying[0]!;
  for (const d of qualifying) {
    if (d.tir.in_range_pct > best.tir.in_range_pct) best = d;
    if (d.tir.in_range_pct < toughest.tir.in_range_pct) toughest = d;
  }

  // Streak: from the most recent day backward while the day qualifies and
  // clears the TIR bar. A no-data / sparse day breaks it (can't confirm).
  let streak = 0;
  for (const d of days) {
    if (d.reading_count < MIN_DAY_READINGS) break;
    if (d.tir.in_range_pct >= STREAK_TIR_PCT) streak++;
    else break;
  }

  return {
    best: { date: best.date, inRangePct: best.tir.in_range_pct },
    toughest: { date: toughest.date, inRangePct: toughest.tir.in_range_pct },
    streak,
  };
}

export interface InsightInputs {
  buckets: EpisodeBuckets;
  /** Total lows+highs in the equal-length period immediately before the
   *  window, or null when not available. */
  priorAlarmTotal: number | null;
  /** Human label for the period length, e.g. "24 hours" or "7 days". */
  periodLabel: string;
}

function mean(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** 2–4 short, strictly DESCRIPTIVE takeaways from the computed stats.
 *  Never prescriptive — describes timing/counts/durations only. */
export function buildInsights(inp: InsightInputs): string[] {
  const { buckets, priorAlarmTotal, periodLabel } = inp;
  const { lowsByHour, highsByHour, lowsTotal, highsTotal, overnightLows, lowDurationsMin } =
    buckets;
  const out: string[] = [];

  const lw = modalWindow(lowsByHour);
  if (lowsTotal >= 2 && lw) {
    out.push(`Low episodes most often occur between ${fmtHourWindow(lw.start, lw.end)}.`);
  }

  if (lowsTotal >= 3 && overnightLows / lowsTotal >= 0.4) {
    out.push(
      `Most lows (${overnightLows} of ${lowsTotal}) happen overnight, before 6 AM.`,
    );
  }

  const hw = modalWindow(highsByHour);
  if (highsTotal >= 2 && hw) {
    out.push(`High episodes cluster between ${fmtHourWindow(hw.start, hw.end)}.`);
  }

  if (priorAlarmTotal != null) {
    const cur = lowsTotal + highsTotal;
    if (cur !== priorAlarmTotal && (cur > 0 || priorAlarmTotal > 0)) {
      const dir = cur < priorAlarmTotal ? "Fewer" : "More";
      out.push(
        `${dir} alarms than the previous ${periodLabel} (${cur} vs ${priorAlarmTotal}).`,
      );
    }
  }

  if (lowDurationsMin.length >= 2) {
    const avg = Math.round(mean(lowDurationsMin));
    out.push(`Low episodes lasted about ${avg} min on average before recovering.`);
  }

  // Fall back to a flat description when nothing notable fired.
  if (out.length === 0) {
    if (lowsTotal === 0 && highsTotal === 0) {
      out.push(`No low or high alarms in this ${periodLabel}.`);
    } else {
      out.push(
        `${lowsTotal} low and ${highsTotal} high ${
          lowsTotal + highsTotal === 1 ? "alarm" : "alarms"
        } in this ${periodLabel}.`,
      );
    }
  }

  return out.slice(0, 4);
}

/** A readable period label from the window's hour count. */
export function periodLabelForHours(hours: number): string {
  if (hours % 24 === 0) {
    const d = hours / 24;
    return d === 1 ? "day" : `${d} days`;
  }
  return hours === 1 ? "hour" : `${hours} hours`;
}
